// DEV-08 批次作業：一次對很多台設備做同一件事（改名、更新韌體），逐台記結果，失敗的可以重試。
// 由瀏覽器執行（頁面開著才會跑完）：同時最多 concurrency 台，每一台各自成功 / 失敗，不因為一台失敗停下來。
// 可以跨來源選設備（SPEC #device-permissions-ui）：沒權限的那幾台先標 denied 並列出原因（照「我的設備」的 actions / can_manage），不執行、不中斷其他台；
// 伺服器照樣逐台驗權。

export type BatchState = 'pending' | 'running' | 'ok' | 'skipped' | 'failed' | 'denied';

export interface BatchRow<T> {
  item: T;
  state: BatchState;
  /** ok / skipped 的說明，或 failed 的錯誤碼 */
  detail?: string;
}

export type BatchOutcome = { state: 'ok' | 'skipped'; detail?: string };

/**
 * 依序（最多 concurrency 個同時）對 rows 裡 state = pending 的每一筆跑 fn；每次狀態變化呼叫 onUpdate（給畫面重畫）。
 * fn 丟錯 = failed（detail = 錯誤碼或訊息）。回傳最後的 rows（同一個陣列，原地更新）。
 */
export async function runBatch<T>(
  rows: BatchRow<T>[],
  fn: (item: T) => Promise<BatchOutcome>,
  onUpdate: (rows: BatchRow<T>[]) => void,
  concurrency = 4,
): Promise<BatchRow<T>[]> {
  const queue = rows.filter((r) => r.state === 'pending');
  let next = 0;
  const worker = async () => {
    for (;;) {
      const row = queue[next++];
      if (!row) return;
      row.state = 'running';
      onUpdate(rows);
      try {
        const out = await fn(row.item);
        row.state = out.state;
        row.detail = out.detail;
      } catch (e) {
        row.state = 'failed';
        row.detail = String((e as { code?: string }).code ?? (e as Error).message ?? e);
      }
      onUpdate(rows);
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(concurrency, queue.length)) }, worker));
  return rows;
}

/** 逐台預先判權：reasonOf 回原因 = 這台沒有權限（denied，detail = 原因），不執行；null = 照常排進去。 */
export function precheck<T>(items: T[], reasonOf: (item: T) => string | null): BatchRow<T>[] {
  return items.map((item) => {
    const why = reasonOf(item);
    return why ? { item, state: 'denied' as const, detail: why } : { item, state: 'pending' as const };
  });
}

/** 失敗的重新排進去（其他不動；沒權限的不重試）。 */
export function retryFailed<T>(rows: BatchRow<T>[]): BatchRow<T>[] {
  return rows.map((r) => (r.state === 'failed' ? { ...r, state: 'pending', detail: undefined } : r));
}

export function batchSummary<T>(rows: BatchRow<T>[]): Record<BatchState, number> {
  const out: Record<BatchState, number> = { pending: 0, running: 0, ok: 0, skipped: 0, failed: 0, denied: 0 };
  for (const r of rows) out[r.state]++;
  return out;
}

/**
 * 改名規則：{name} = 原本的名稱、{n} = 序號（從 start 起）、{n:02} = 補零到 2 位。例如「1F-{n:02} {name}」。
 * 結果去頭尾空白；空的或超過 64 字回 null（這一台不改）。
 */
export function renameByRule(rule: string, name: string, index: number, start = 1): string | null {
  const n = start + index;
  const out = rule
    .replace(/\{name\}/g, name)
    .replace(/\{n(?::0?(\d))?\}/g, (_m, w?: string) => String(n).padStart(w ? Number(w) : 1, '0'))
    .trim();
  return out && [...out].length <= 64 ? out : null;
}
